<script lang="ts">
  import { onMount } from 'svelte';
  import Button from '../../../Button.svelte';
  import PaymentReportService from '../../../../lib/api/employer/payment-report';
  import toast from 'svelte-french-toast';
  import { api } from '../../../../lib/api/client';
  import { refreshEmployerPendingActions } from '../../../../stores/employer-pending-actions.store';

  // Combined payment data
  interface CharityDonators {
    charity: {
      uuid: string;
      name: string;
    };
    donators: Array<{
      uuid: string;
      name: string;
      hours: number;
      amount: number;
    }>;
    totalAmount: number;
    totalHours: number;
  }

  let combinedPayment = {
    totalAmount: 0,
    totalDonations: 0,
    totalHours: 0,
    charities: [] as CharityDonators[]
  };

  // State
  let isLoading = true;
  let error = '';
  let expandedCharities: Record<string, boolean> = {};

  function toggleCharity(charityUuid: string) {
    const currentState = expandedCharities[charityUuid] || false;
    // Create a new object to trigger reactivity
    expandedCharities = {
      ...expandedCharities,
      [charityUuid]: !currentState
    };
    console.log('Toggled charity:', charityUuid, 'New state:', !currentState);
    console.log('Expanded charities:', expandedCharities);
  }

  // Reactive function to check if charity is expanded
  function isCharityExpanded(charityUuid: string): boolean {
    // Access expandedCharities to make this reactive
    return expandedCharities[charityUuid] === true;
  }

  async function loadInvoices() {
    try {
      isLoading = true;
      error = '';
      
      const response = await PaymentReportService.getOutstandingPayments();
      
      if (response.success && response.data) {
        const data = response.data;
        
        // Map the API response to our component structure
        combinedPayment = {
          totalAmount: data.totalAmount || 0,
          totalDonations: data.totalDonations || 0,
          totalHours: data.totalHours || 0,
          charities: (data.charities || []).map((charityGroup: any) => ({
            charity: charityGroup.charity,
            donators: charityGroup.donators || [],
            totalAmount: charityGroup.totalAmount || 0,
            totalHours: charityGroup.totalHours || 0
          }))
        };
      } else {
        error = response.message || 'Failed to load outstanding payments';
        combinedPayment = {
          totalAmount: 0,
          totalDonations: 0,
          totalHours: 0,
          charities: []
        };
      }
    } catch (err: any) {
      error = 'Failed to load outstanding payments';
      toast.error(error);
      console.error('Error loading outstanding payments:', err);
      combinedPayment = {
        totalAmount: 0,
        totalDonations: 0,
        totalHours: 0,
        charities: []
      };
    } finally {
      isLoading = false;
    }
  }

  async function handleDownloadInvoice() {
    try {
      isLoading = true;
      const response = await api.get<Blob>(`/outstanding-payments/invoice`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data as BlobPart], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `outstanding-payments-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Payment summary downloaded successfully');
    } catch (err: any) {
      console.error('Error downloading payment summary:', err);
      toast.error(err.response?.data?.message || 'Failed to download payment summary');
    } finally {
      isLoading = false;
    }
  }

  async function handleMarkAsPaid() {
    try {
      isLoading = true;
      const response = await api.post<{ success: boolean; message?: string }>(`/outstanding-payments/mark-paid`);
      
      if (response.data.success) {
        toast.success('All outstanding payments marked as paid successfully');
        await refreshEmployerPendingActions();
        loadInvoices(); // Reload to refresh the list
      } else {
        toast.error(response.data.message || 'Failed to mark payments as paid');
      }
    } catch (err: any) {
      console.error('Error marking payment as paid:', err);
      toast.error(err.response?.data?.message || 'Failed to mark payment as paid');
    } finally {
      isLoading = false;
    }
  }

  function formatCurrency(amount: number | string): string {
    return `$${parseFloat(String(amount || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  onMount(() => {
    loadInvoices();
  });
</script>

<div class="outstanding-invoices-page">
  <div class="payment-section">
    {#if error}
      <div class="error-message">
        <p>{error}</p>
        <Button variant="secondary" on:click={loadInvoices}>Try Again</Button>
      </div>
    {:else}
      {#if isLoading}
        <div class="loading-message">Loading payment information...</div>
      {:else if combinedPayment.totalDonations === 0}
        <div class="empty-message">
          <h3>No Payment Due</h3>
          <p>All approved donations have been paid.</p>
        </div>
      {:else}
        <div class="payment-card">
          <div class="payment-header">
            <div class="payment-info">
              <h2 class="payment-title">Pending Payments</h2>
              <div class="payment-summary">
                <span>{combinedPayment.totalDonations} donation{combinedPayment.totalDonations !== 1 ? 's' : ''}</span>
                <span class="separator">•</span>
                <span>{combinedPayment.totalHours} hours</span>
                <span class="separator">•</span>
                <span class="total-amount">Total: {formatCurrency(combinedPayment.totalAmount)}</span>
              </div>
            </div>
            <div class="payment-actions">
              <Button 
                variant="secondary" 
                on:click={handleDownloadInvoice}
                disabled={isLoading}
              >
                Download Summary
              </Button>
              <Button 
                variant="primary" 
                on:click={handleMarkAsPaid}
                disabled={isLoading}
              >
                Mark As Paid
              </Button>
            </div>
          </div>

          <!-- Donators grouped by charity -->
          <div class="donators-section">
            {#each combinedPayment.charities as charityGroup (charityGroup.charity.uuid)}
              <div class="charity-group">
                <button 
                  class="charity-header-button"
                  on:click|stopPropagation={() => {
                    toggleCharity(charityGroup.charity.uuid);
                  }}
                  type="button"
                  aria-expanded={expandedCharities[charityGroup.charity.uuid] || false}
                >
                  <div class="charity-header">
                    <div class="charity-header-content">
                      <h3 class="charity-name">{charityGroup.charity.name}</h3>
                      <div class="charity-summary">
                        <span>{charityGroup.donators.length} donation{charityGroup.donators.length !== 1 ? 's' : ''}</span>
                        <span class="separator">•</span>
                        <span>{charityGroup.totalHours} hours</span>
                        <span class="separator">•</span>
                        <span class="charity-total">{formatCurrency(charityGroup.totalAmount)}</span>
                      </div>
                    </div>
                    <svg 
                      class="expand-icon" 
                      class:expanded={expandedCharities[charityGroup.charity.uuid]}
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      stroke-width="2" 
                      stroke-linecap="round" 
                      stroke-linejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                </button>
                {#if expandedCharities[charityGroup.charity.uuid]}
                  <div class="donators-list">
                    {#each charityGroup.donators as donator (donator.uuid)}
                      <div class="donator-item">
                        <div class="donator-info">
                          <span class="donator-name">{donator.name}</span>
                          <span class="donator-hours">{donator.hours} hours</span>
                        </div>
                        <div class="donator-amount">{formatCurrency(donator.amount)}</div>
                      </div>
                    {/each}
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .outstanding-invoices-page {
    padding: 0;
    max-width: 100%;
  }

  .payment-section {
    display: flex;
    flex-direction: column;
  }

  .payment-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    border: 1px solid #f3f4f6;
  }

  .payment-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 2rem;
    padding-bottom: 1.5rem;
    border-bottom: 2px solid #e5e7eb;
    gap: 1rem;
  }

  .payment-info {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    flex: 1;
  }

  .payment-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .payment-summary {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #6b7280;
  }

  .separator {
    color: #d1d5db;
  }

  .total-amount {
    font-weight: 600;
    color: #111827;
    font-size: 1rem;
  }

  .payment-actions {
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;
    flex-wrap: nowrap;
    align-items: center;
  }

  .payment-actions :global(button) {
    white-space: nowrap;
  }

  .donators-section {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .charity-group {
    background: #f9fafb;
    border-radius: 8px;
    padding: 0;
    border: 1px solid #e5e7eb;
    overflow: hidden;
  }

  .charity-header-button {
    width: 100%;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-align: left;
    transition: background-color 0.2s;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    z-index: 1;
    position: relative;
  }

  .charity-header-button:hover {
    background-color: #f3f4f6;
  }

  .charity-header-button:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
    background-color: #f3f4f6;
  }

  .charity-header-button:active {
    background-color: #e5e7eb;
  }

  .charity-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem;
    margin: 0;
    border-bottom: 1px solid #e5e7eb;
    pointer-events: none;
  }

  .charity-header-content {
    flex: 1;
    pointer-events: none;
  }

  .charity-header-content * {
    pointer-events: none;
  }

  .charity-name {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.5rem 0;
  }

  .expand-icon {
    flex-shrink: 0;
    color: #6b7280;
    transition: transform 0.2s ease;
    margin-left: 1rem;
  }

  .expand-icon.expanded {
    transform: rotate(180deg);
  }

  .charity-summary {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #6b7280;
  }

  .charity-total {
    font-weight: 600;
    color: #111827;
  }

  .donators-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1.25rem;
    padding-top: 0.75rem;
    animation: slideDown 0.2s ease-out;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
    }
    to {
      opacity: 1;
      max-height: 1000px;
    }
  }

  .donator-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem;
    background: white;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
  }

  .donator-info {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .donator-name {
    font-size: 0.875rem;
    font-weight: 500;
    color: #111827;
  }

  .donator-hours {
    font-size: 0.875rem;
    color: #6b7280;
  }

  .donator-amount {
    font-size: 0.875rem;
    font-weight: 600;
    color: #111827;
  }

  .error-message {
    text-align: center;
    padding: 3rem;
    color: #ef4444;
  }

  .error-message p {
    margin-bottom: 1rem;
    font-size: 1rem;
  }

  .loading-message,
  .empty-message {
    text-align: center;
    padding: 3rem;
    color: #6b7280;
  }

  .empty-message h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.5rem;
  }

  .empty-message p {
    font-size: 0.875rem;
    color: #6b7280;
  }

  @media (max-width: 768px) {
    .payment-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .payment-actions {
      width: 100%;
      flex-direction: row;
      justify-content: flex-start;
    }

    .payment-actions :global(button) {
      flex: 1;
      min-width: 0;
    }
  }
</style>
